import _omit from 'lodash/omit';

import MESSAGES from 'constants/messages';
import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getCurrentPage } from 'store/utils/pagination';
import {
  partialExportAction,
  cancelPartialExportAction,
  partialImportAndMergeAction,
  partialImportAndReplaceAction,
} from 'store/shared/actions';
import * as api from './api';
import * as types from './actionTypes';
import * as selectors from './selectors';

/**
 * Check if a context already exists
 *
 * @param fieldName
 * @param fieldValue
 * @param exception
 * @returns {Function}
 */
export const checkContextName = (
  fieldName,
  fieldValue,
  exception = null,
) => async () => {
  try {
    const { data } = await api.checkContextNameRequest(fieldValue, exception);
    if (data.count > 0) {
      // eslint-disable-next-line no-throw-literal
      throw {
        [fieldName]: MESSAGES.CONTEXTS.NAME.EXISTS,
      };
    }
  } catch (err) {
    throw err;
  }
};

/**
 * Get all contexts
 */
export const getContextsAll = (params = {}) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_CONTEXT_ALL_REQUEST,
    });

    const { data } = await api.getContextsRequest({
      ordering: 'name',
      size: 30,
      ...params,
    });

    const contexts = data.results.map(item => ({
      ...item,
      isSelected: selectors.getContextsAllObject(getState())[item.id] || false,
    }));

    dispatch({
      type: types.GET_CONTEXT_ALL_SUCCESS,
      payload: {
        items: contexts,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_CONTEXT_ALL_ERROR,
      payload: {
        error: error.message,
      },
    });
  }
};

/**
 * Get paginated contexts
 *
 * @param params
 */
export const getContextsList = (params = {}) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_CONTEXT_LIST_REQUEST,
    });

    const finalParams = {
      ...selectors.getPaginationOptions(getState()),
      ...params,
    };

    dispatch({
      type: types.UPDATE_CONTEXT_LIST_PAGINATION_OPTIONS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    // TODO: Drop "_omit" when API supports proper ordering for contexts
    const { data } = await api.getContextsRequest(
      _omit(finalParams, ['ordering']),
    );

    dispatch({
      type: types.GET_CONTEXT_LIST_SUCCESS,
      payload: {
        items: data.results,
        count: data.count,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_CONTEXT_LIST_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add a context
 *
 * @param data
 */
export const addContext = data => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.ADD_CONTEXT_REQUEST,
    });

    const response = await api.addContextRequest(data);

    await dispatch({
      type: types.ADD_CONTEXT_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    const { size } = selectors.getPaginationOptions(getState());
    const count = selectors.getContextsListCount(getState());
    const calculateContextsLastPage = Math.ceil((count + 1) / size);

    // TODO: Change this if contexts sorting changes
    // We redirect the user to the last page because contexts
    // are fetched with ascending created_at order by API
    await dispatch(getContextsList({ page: calculateContextsLastPage }));

    Notify.success({
      title: 'Context was created successfully',
    });

    await dispatch(getContextsAll());

    // FIXME: call selectContext instead of? Check how it works with routing.
    return response.data.id;
  } catch (error) {
    await dispatch({
      type: types.ADD_CONTEXT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to Add Content',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

/**
 * Get context
 *
 * @param id
 */
export const getContext = id => async dispatch => {
  try {
    await dispatch({
      type: types.GET_CONTEXT_REQUEST,
    });

    const { data } = await api.getContextRequest(id);

    await dispatch({
      type: types.GET_CONTEXT_SUCCESS,
      payload: {
        item: data,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_CONTEXT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update context
 *
 * @param id
 * @param data
 */
export const updateContext = (id, data) => async dispatch => {
  try {
    await dispatch({
      type: types.UPDATE_CONTEXT_REQUEST,
    });

    const response = await api.updateContextRequest(id, data);

    await dispatch({
      type: types.UPDATE_CONTEXT_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    await dispatch(getContextsList({ page: 1 }));

    Notify.success({
      title: 'Context was updated successfully',
    });

    await dispatch(getContextsAll());
  } catch (error) {
    await dispatch({
      type: types.UPDATE_CONTEXT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Context',
      description: ErrorHandler.getNotificationError(error),
    });

    throw error;
  }
};

/**
 * Delete a context
 *
 * @param id
 */
export const deleteContext = id => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.DELETE_CONTEXT_REQUEST,
    });

    await api.deleteContextRequest(id);

    await dispatch({
      type: types.DELETE_CONTEXT_SUCCESS,
    });

    await dispatch(
      getContextsList({
        page: getCurrentPage(
          selectors.getPaginationOptions(getState()).page,
          selectors.getContextsList(getState()),
        ),
      }),
    );

    Notify.success({
      title: 'Context was deleted successfully',
    });

    await dispatch(getContextsAll());
  } catch (error) {
    await dispatch({
      type: types.DELETE_CONTEXT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Context',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Select a context
 *
 * @param item
 */
export const selectContext = item => ({
  type: types.SELECT_CONTEXT,
  payload: {
    item,
  },
});

/**
 * Deselect a context
 */
export const deselectContext = () => ({
  type: types.DESELECT_CONTEXT,
});

/**
 * Select a context from all
 *
 * @param item
 */
export const selectContextFromAll = item => (dispatch, getState) => {
  const contexts = selectors.getContextsAll(getState()).map(context => ({
    ...context,
    isSelected: context.id === item.id,
  }));

  dispatch({
    type: types.UPDATE_CONTEXT_ALL,
    payload: {
      contexts,
    },
  });
};

/**
 * Deselect a context from all
 */
export const deselectContextFromAll = () => (dispatch, getState) => {
  const contexts = selectors.getContextsAll(getState()).map(context => ({
    ...context,
    isSelected: false,
  }));

  dispatch({
    type: types.UPDATE_CONTEXT_ALL,
    payload: {
      contexts,
    },
  });
};

export const partialImportAndMergeContexts = partialImportAndMergeAction({
  path: 'contexts',
  postHook: dispatch => dispatch(getContextsList({ page: 1 })),
  types: {
    complete: types.PARTIAL_IMPORT_CONTEXT_COMPLETE,
    request: types.PARTIAL_IMPORT_CONTEXT_REQUEST,
  },
});

export const partialImportAndReplaceContexts = partialImportAndReplaceAction({
  path: 'contexts',
  postHook: dispatch => dispatch(getContextsList({ page: 1 })),
  types: {
    complete: types.PARTIAL_IMPORT_CONTEXT_COMPLETE,
    request: types.PARTIAL_IMPORT_CONTEXT_REQUEST,
  },
});

export const partialExportSingleContext = partialExportAction({
  path: 'contexts',
  filename: 'context',
  types: {
    complete: types.PARTIAL_EXPORT_CONTEXT_COMPLETE,
    request: types.PARTIAL_EXPORT_CONTEXT_REQUEST,
  },
});

export const partialExportContext = partialExportAction({
  path: 'contexts',
  types: {
    complete: types.PARTIAL_EXPORT_CONTEXT_COMPLETE,
    request: types.PARTIAL_EXPORT_CONTEXT_REQUEST,
  },
});

export const cancelExportContexts = cancelPartialExportAction({
  path: 'contexts',
  types: {
    cancel: types.PARTIAL_EXPORT_CONTEXT_CANCEL,
  },
});
